import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import Spinner from '../components/Spinner';
import { clearAllAuthCache, clearGoogleAuthCache, forceRefreshAuth, debugAuthCache } from '../utils/authCache';

const AdminLoginPage = ({ darkMode }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Authorized admin emails
  const AUTHORIZED_ADMIN_EMAILS = ['doctorworldconsultancy@gmail.com', 'officialvishwateja@gmail.com'];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        console.log('User authenticated:', firebaseUser.email);
        setUser(firebaseUser);
        
        // Check if authorized admin
        if (AUTHORIZED_ADMIN_EMAILS.includes(firebaseUser.email)) {
          console.log('Authorized admin detected, redirecting to dashboard...');
          navigate('/admin-dashboard');
        } else {
          console.log('Unauthorized user, access denied...');
          // UI will show access denied message - no need for error state
        }
      } else {
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Starting Google authentication...');
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      console.log('Google login successful:', user.email);
      
      // Check if authorized admin email
      if (AUTHORIZED_ADMIN_EMAILS.includes(user.email)) {
        console.log('Admin authorized, redirecting...');
        // The auth state change will handle the redirect
      } else {
        console.log('Unauthorized email:', user.email);
        // UI will show access denied message - no need for error state
      }
      
    } catch (error) {
      console.error('Google login error:', error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Login cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        setError('Popup blocked. Please allow popups and try again.');
      } else {
        setError(`Login failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setError('');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${
      darkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-indigo-50'
    }`}>
      <div className="max-w-md w-full space-y-8">
        {/* Header Section */}
        <div className="text-center">
          <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-6 ${
            darkMode ? 'bg-indigo-600' : 'bg-indigo-600'
          }`}>
            <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className={`text-3xl font-bold ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Admin Portal
          </h2>
          <p className={`mt-2 text-sm ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Secure access to manage applications and student data
          </p>
        </div>

        {/* Login Section */}
        <div className={`p-8 rounded-2xl shadow-2xl backdrop-blur-sm ${
          darkMode 
            ? 'bg-gray-800/90 border border-gray-700' 
            : 'bg-white/80 border border-gray-200'
        }`}>
          {error && (
            <div className={`mb-6 p-4 rounded-lg border-l-4 ${
              darkMode 
                ? 'bg-red-900/20 border-red-500 text-red-400'
                : 'bg-red-50 border-red-400 text-red-700'
            }`}>
              <div className="flex">
                <svg className="h-5 w-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">{error}</span>
              </div>
            </div>
          )}

          {user ? (
            // User is logged in but not authorized - just show access denied
            <div className="text-center space-y-4">
              <div className={`p-4 rounded-lg border ${
                darkMode 
                  ? 'bg-red-900/20 border-red-500/30 text-red-200' 
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <p className="font-medium">Access Denied</p>
                <p className="text-sm mt-1">This account is not authorized to access the admin portal.</p>
              </div>
            </div>
          ) : (
            // Not logged in - show Google login
            <div className="space-y-6">
              <div className="text-center">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Sign in with your authorized Google account
                </p>
              </div>

              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl"
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <svg className="h-5 w-5 text-red-200 group-hover:text-red-100" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                </span>
                {loading ? 'Signing in...' : 'Sign in with Google'}
              </button>

              <div className={`text-center text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <p>Only authorized email can access this portal</p>
              </div>

              {/* Developer Tools - Authentication Cache Management */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
                  🛠️ Having login issues? Try clearing authentication cache:
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <button
                    onClick={() => {
                      clearGoogleAuthCache();
                      alert('Google auth cache cleared! Please try logging in again.');
                    }}
                    className={`px-3 py-1 text-xs rounded ${
                      darkMode 
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    } transition-colors`}
                  >
                    Clear Google Cache
                  </button>
                  <button
                    onClick={() => {
                      clearAllAuthCache();
                      alert('All auth cache cleared! Please try logging in again.');
                    }}
                    className={`px-3 py-1 text-xs rounded ${
                      darkMode 
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    } transition-colors`}
                  >
                    Clear All Cache
                  </button>
                  <button
                    onClick={() => {
                      forceRefreshAuth();
                    }}
                    className={`px-3 py-1 text-xs rounded ${
                      darkMode 
                        ? 'bg-red-700 hover:bg-red-600 text-red-200' 
                        : 'bg-red-200 hover:bg-red-300 text-red-700'
                    } transition-colors`}
                  >
                    Force Refresh
                  </button>
                </div>
                <button
                  onClick={debugAuthCache}
                  className={`mt-2 px-3 py-1 text-xs rounded ${
                    darkMode 
                      ? 'bg-blue-700 hover:bg-blue-600 text-blue-200' 
                      : 'bg-blue-200 hover:bg-blue-300 text-blue-700'
                  } transition-colors`}
                >
                  🔍 Debug Auth State (Check Console)
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className={`text-xs ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            Secure admin access • Protected by enterprise security
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
